import qs from 'query-string';
import { createApi } from '@reduxjs/toolkit/query/react';
import { appEndpoint, workflowRunEndpoint } from '@/helpers/apiHelpers';
import type { TimelineEventsEntityRequest, TimelineEventsHistoryEntityRequest } from '@/actions/entities';
import type { TimelineEventsHistoryPaginatedResponse } from '@/actions/timelineEventsHistory';
import { type BaseAPIResponse, baseQuery } from './common';

export interface TimelineEventsAPIRequest extends TimelineEventsHistoryEntityRequest, TimelineEventsEntityRequest {}

export const paginationApi = createApi({
    baseQuery: baseQuery,
    reducerPath: 'paginationApi',
    tagTypes: ['TimelineEvents'],
    endpoints: (builder) => ({
        getTimelineEventsHistory: builder.query<
            BaseAPIResponse<TimelineEventsHistoryPaginatedResponse>,
            TimelineEventsAPIRequest
        >({
            query: ({ orgId, appId, releaseId, releaseStepType, nextCursor }) => {
                const encodedQueryParams = qs.stringify(
                    {
                        releaseId: releaseId ?? '',
                        releaseStepType: releaseStepType ?? '',
                        timelineEventsCursor: nextCursor ?? '',
                    },
                    { skipNull: true, skipEmptyString: true },
                );

                const appUrl = appEndpoint(orgId, appId);
                return `${appUrl}/timelineEventsHistory?${encodedQueryParams}`;
            },
            providesTags: (_result, _error, { orgId, appId, releaseId, releaseStepType }) => [
                {
                    type: 'TimelineEvents',
                    id: `${orgId}-${appId}-${releaseId || 'all'}-${releaseStepType || 'all'}`,
                },
            ],
        }),
        getTimelineEvents: builder.query<
            BaseAPIResponse<TimelineEventsHistoryPaginatedResponse>,
            TimelineEventsAPIRequest
        >({
            query: ({ orgId, workflowId, workflowRunId, stepRunIds, nextCursor }) => {
                const encodedQueryParams = qs.stringify(
                    {
                        stepRunIds: stepRunIds ?? [],
                        timelineEventsCursor: nextCursor ?? '',
                    },
                    { skipNull: true, skipEmptyString: true },
                );

                const workflowUrl = workflowRunEndpoint({ orgId, workflowId, workflowRunId });
                return `${workflowUrl}/timelineEvents?${encodedQueryParams}`;
            },
            providesTags: (_result, _error, { orgId, workflowId, workflowRunId }) => [
                {
                    type: 'TimelineEvents',
                    id: `${orgId}-${workflowId}-${workflowRunId || 'all'}`,
                },
            ],
        }),
    }),
});

export const {
    useGetTimelineEventsHistoryQuery,
    useGetTimelineEventsQuery,
    useLazyGetTimelineEventsHistoryQuery,
    useLazyGetTimelineEventsQuery,
} = paginationApi;
